import type { Alarma } from '../../domain/entities/Alarma';
import type { CriticidadValor } from '../../domain/value-objects/Criticidad';
import { traducirErrorSupabase } from '../../domain/errores';
import { aAlarma, type FilaAlarma } from '../mappers/alarmaMapper';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';
import type { Pagina } from './consultas';

/** Conjunto de estados que el usuario puede pedir desde la interfaz. */
export type FiltroEstadoAlarma = 'abiertas' | 'activa' | 'reconocida' | 'resuelta' | 'todas';

export interface FiltrosAlarmas {
  estado?: FiltroEstadoAlarma;
  clienteId?: number | null;
  areaId?: number | null;
  nodoId?: number | null;
  /** Vacío significa «cualquier criticidad». */
  criticidades?: readonly CriticidadValor[];
  /** `analogico` o `digital`; vacío significa ambos. */
  tipoUmbral?: 'analogico' | 'digital' | null;
  desde?: Date | null;
  hasta?: Date | null;
  pagina?: number;
  porPagina?: number;
}

const VISTA = 'vw_alarmas_frontend';

/**
 * Acceso a las alarmas.
 *
 * La lectura va contra `vw_alarmas_frontend`, que ya trae resueltos los
 * nombres de sensor, nodo, área y cliente. Reconocer y resolver pasan por
 * funciones del servidor, que son las que aplican las reglas y dejan traza.
 */
export class RepositorioAlarmas {
  /**
   * Lista alarmas con paginación en el servidor.
   *
   * La versión anterior traía hasta 500 filas de una vez y recortaba en el
   * navegador; aquí sólo viaja la página que se está mirando.
   */
  async listar(filtros: FiltrosAlarmas = {}): Promise<Pagina<Alarma>> {
    const { pagina = 1, porPagina = 25 } = filtros;

    let consulta = filtroDemo.aplicar(VISTA, supabase.from(VISTA).select('*', { count: 'exact' }));

    // «Abiertas» agrupa activas y reconocidas: es lo que un operador entiende
    // por «lo que todavía me toca atender».
    if (filtros.estado === 'abiertas') consulta = consulta.in('estado', ['activa', 'reconocida']);
    else if (filtros.estado && filtros.estado !== 'todas') {
      consulta = consulta.eq('estado', filtros.estado);
    }

    consulta = ambitoSesion.aplicar(consulta);
    if (filtros.clienteId) consulta = consulta.eq('cliente_id', filtros.clienteId);
    if (filtros.areaId) consulta = consulta.eq('area_id', filtros.areaId);
    if (filtros.nodoId) consulta = consulta.eq('nodo_id', filtros.nodoId);
    if (filtros.tipoUmbral) consulta = consulta.eq('tipo_umbral', filtros.tipoUmbral);

    if (filtros.criticidades?.length) {
      consulta = consulta.in('criticidad', [...filtros.criticidades]);
    }

    if (filtros.desde) consulta = consulta.gte('timestamp_inicio', filtros.desde.toISOString());
    if (filtros.hasta) consulta = consulta.lte('timestamp_inicio', filtros.hasta.toISOString());

    consulta = consulta.order('timestamp_inicio', { ascending: false });

    const desde = (pagina - 1) * porPagina;
    consulta = consulta.range(desde, desde + porPagina - 1);

    const { data, error, count } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    return {
      filas: ((data ?? []) as FilaAlarma[]).map(aAlarma),
      total: count ?? 0,
    };
  }

  /**
   * Recupera una alarma concreta.
   * Se usa al recibir un aviso en tiempo real, porque el evento sólo trae la
   * fila de la tabla base y no los nombres que necesita la pantalla.
   */
  async obtenerPorId(id: string): Promise<Alarma | null> {
    const { data, error } = await supabase.from(VISTA).select('*').eq('id', id).maybeSingle();

    if (error) throw traducirErrorSupabase(error);
    return data ? aAlarma(data as FilaAlarma) : null;
  }

  /** Cuenta alarmas abiertas por criticidad, para los indicadores de cabecera. */
  async contarAbiertasPorCriticidad(
    clienteId?: number | null,
  ): Promise<Record<CriticidadValor, number>> {
    let consulta = filtroDemo.aplicar(
      VISTA,
      supabase.from(VISTA).select('criticidad').in('estado', ['activa', 'reconocida']),
    );

    consulta = ambitoSesion.aplicar(consulta);
    if (clienteId) consulta = consulta.eq('cliente_id', clienteId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    const conteo: Record<CriticidadValor, number> = { critica: 0, alta: 0, media: 0, baja: 0 };

    for (const fila of (data ?? []) as { criticidad?: unknown }[]) {
      const clave = String(fila.criticidad ?? '').toLowerCase();
      if (clave in conteo) conteo[clave as CriticidadValor] += 1;
    }

    return conteo;
  }

  /**
   * Marca la alarma como vista.
   * La función del servidor registra quién y cuándo; por eso no se actualiza
   * la tabla directamente.
   */
  async reconocer(id: string): Promise<void> {
    const { error } = await supabase.rpc('v2_reconocer_alarma', { p_alarma_id: id });
    if (error) throw traducirErrorSupabase(error);
  }

  /** Cierra la alarma. */
  async resolver(id: string): Promise<void> {
    const { error } = await supabase.rpc('v2_resolver_alarma', { p_alarma_id: id });
    if (error) throw traducirErrorSupabase(error);
  }
}

export const repositorioAlarmas = new RepositorioAlarmas();
